import { useCallback, useState } from 'react';
import { API_BASE } from '../../../config/constants';

function buildJsonHeaders(): Record<string, string> {
	return { 'Content-Type': 'application/json' };
}

export function useTableData<T = any>(table: string) {
	const [data, setData] = useState<T[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const loadTableData = useCallback(async () => {
		console.log('Fetching table data for:', table); // Log table name
		setLoading(true);
		setError(null);
		try {
			const response = await fetch(`${API_BASE}/api/data/get-table-data`, {
				method: 'POST',
				headers: buildJsonHeaders(),
				body: JSON.stringify({ table }),
			});
			console.log('Response status:', response.status); // Log response status
			if (!response.ok) {
				throw new Error('Failed to fetch table data');
			}
			const result = await response.json();
			console.log('Fetched data:', result); // Log fetched data
			setData(Array.isArray(result.data) ? result.data : []);
		} catch (err: any) {
			console.error('Error loading table data:', err); // Log error
			setError(err.message || 'Failed to load table data');
			setData([]);
		} finally {
			setLoading(false);
		}
	}, [table]);

	return { data, loading, error, loadTableData };
}

const getFieldValue = (row: Record<string, any>, fieldNames: string[]): any => {
	for (const fieldName of fieldNames) {
		const exact = row[fieldName];
		if (exact !== undefined) return exact;
		const matchedKey = Object.keys(row).find((key) => key.toLowerCase() === fieldName.toLowerCase());
		if (matchedKey) return row[matchedKey];
	}
	return undefined;
};

const toNumericYield = (value: unknown): number | null => {
	if (value === null || value === undefined || value === '') return null;
	const sanitized = String(value).replace('%', '').trim();
	const parsed = Number(sanitized);
	return Number.isFinite(parsed) ? parsed : null;
};

export function getFilteredYieldGross(
	data: any[],
	assetClass: string,
	country: string
): number[] {
	return data
		.filter((row) => {
			const classValue = String(getFieldValue(row, ['ASSET_CLASS', 'asset_class']) ?? '')
				.trim()
				.toLowerCase();
			const countryValue = String(getFieldValue(row, ['Country', 'country']) ?? '')
				.trim()
				.toLowerCase();
			return classValue === assetClass.toLowerCase() && countryValue === country.toLowerCase();
		})
		.map((row) => toNumericYield(getFieldValue(row, ['yield_gross', 'YIELD_GROSS'])))
		.filter((yield_gross) => yield_gross !== null) as number[];
}
